import { Dispatch, SetStateAction, useState } from 'react';
import { SharedDriveModel } from '../../../../../../../types';
import SharedDrivesAutocomplete from '../../../../../../shared/shared-drives-autocomplete';
import SharedDrivesBigQueryAutocomplete from '../../../../../../shared/shared-drive-bigquery-autocomplete';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import SharedDrivesPermissionCheckDialog from '../../shared-drives/shared-drives-parent/shared-drives-permission-check-dialog';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import {
  getUserPermission,
  idAccessToken,
  UserKeys,
} from '../../../../../../api/users-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useDrivePicker from 'react-google-drive-picker';
import { handleErrors } from '../../../../../../utils/errors';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import DriveFolderUpload from '@mui/icons-material/DriveFolderUpload';

interface SharedDrivesChildMoveDialogContentProps {
  autocompleteValue: string;
  setAutocompleteValue: Dispatch<SetStateAction<string>>;
  selectedDrive: SharedDriveModel | null;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  selectedDrivePickerFolder: CallbackDoc | null;
  setSelectedDrivePickerFolder: Dispatch<SetStateAction<CallbackDoc | null>>;
  selectedFolder: string;
  selectedPickerFolderIsInDocs: boolean;
  openChangeLogDialog: boolean;
  setOpenChangeLogDialog: Dispatch<SetStateAction<boolean>>;
  changeLogMessage: string;
  setChangeLogMessage: Dispatch<SetStateAction<string>>;
  mutate: (id: string) => void;
  isMoving: boolean;
  openPermissionCheckDialog: boolean;
  setOpenPermissionCheckDialog: Dispatch<SetStateAction<boolean>>;
  changeLogRoles: string[];
  setPermissionCheckLoading: Dispatch<SetStateAction<boolean>>;
}

export default function SharedDrivesChildMoveDialogContent({
  autocompleteValue,
  setAutocompleteValue,
  selectedDrive,
  setSelectedDrive,
  selectedDrivePickerFolder,
  setSelectedDrivePickerFolder,
  selectedFolder,
  selectedPickerFolderIsInDocs,
  openChangeLogDialog,
  setOpenChangeLogDialog,
  changeLogMessage,
  setChangeLogMessage,
  mutate,
  isMoving,
  openPermissionCheckDialog,
  setOpenPermissionCheckDialog,
  changeLogRoles,
  setPermissionCheckLoading,
}: SharedDrivesChildMoveDialogContentProps) {
  const { loggedInUser, currentTier } = useUserSettings();
  const queryClient = useQueryClient();

  const [openingPicker, setOpeningPicker] = useState(false);

  const [openPicker] = useDrivePicker();

  const { data: accessToken } = useQuery({
    queryKey: [UserKeys.AccessToken],
    queryFn: () => idAccessToken(),
  });

  const handleOpenPicker = async () => {
    try {
      const hasPermission = await queryClient.fetchQuery({
        queryKey: [
          UserKeys.SharedDriveUserPermission,
          loggedInUser,
          selectedDrive?.id,
        ],
        queryFn: () =>
          getUserPermission(
            selectedDrive?.id || selectedFolder,
            loggedInUser,
            true
          ),
      });

      if (!hasPermission) {
        // This is the line that was causing the error.
        setOpeningPicker(true);
        setOpenPermissionCheckDialog(true);
      } else {
        setOpeningPicker(false);
        openPicker({
          clientId: '',
          developerKey: import.meta.env.VITE_DRIVE_PICKER_API_KEY,
          token: accessToken,
          viewId: 'FOLDERS',
          setParentFolder: selectedDrive?.id,
          setSelectFolderEnabled: true,
          callbackFunction: (data) => {
            const elements = Array.from(
              document.getElementsByClassName(
                'picker-dialog'
              ) as HTMLCollectionOf<HTMLElement>
            );
            for (let i = 0; i < elements.length; i++) {
              elements[i].style.zIndex = '2000';
            }
            if (data.action === 'picked' && data.docs[0]) {
              setSelectedDrivePickerFolder(data.docs[0]);
            }
          },
        });
      }
    } catch (e) {
      handleErrors({
        error: e,
        snackbarMessage: `There was an issue checking your permissions. Please try again.`,
      });
    } finally {
      setPermissionCheckLoading(false);
    }
  };

  return (
    <Box sx={{ px: 4 }}>
      {currentTier !== 'enterprise' ? (
        <Tooltip
          arrow
          title="This searches for a shared drive whose title contains the word you are typing in. Ex: name contains 'confidential' when typing confidential"
        >
          <Box sx={{ mb: 2 }}>
            <SharedDrivesAutocomplete
              autocompleteValue={autocompleteValue}
              setAutoCompleteValue={setAutocompleteValue}
              onDriveSelect={(drive) => {
                setSelectedDrive(drive);
                if (!drive) {
                  setSelectedDrivePickerFolder(null);
                }
              }}
              size="medium"
            />
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ mb: 2 }}>
          <SharedDrivesBigQueryAutocomplete
            autocompleteValue={autocompleteValue}
            setAutoCompleteValue={setAutocompleteValue}
            onDriveSelect={(drive) => {
              setSelectedDrive(drive);
              if (!drive) {
                setSelectedDrivePickerFolder(null);
              }
            }}
            size="medium"
          />
        </Box>
      )}
      {selectedDrive && (
        <>
          {selectedDrive.id === selectedFolder ? (
            <Typography>
              Select a folder from within the selected drive you'd like to move
              the documents to.
            </Typography>
          ) : (
            <Typography>
              Optionally select a folder from within the selected drive you'd
              like to move the documents to.
            </Typography>
          )}
          <Stack flexDirection={'row'} gap={4} sx={{ mt: 2 }}>
            <Box pl={4} width={220}>
              <Button
                component="label"
                variant="contained"
                startIcon={<DriveFolderUpload />}
                onClick={async () => {
                  await handleOpenPicker();
                }}
              >
                Select Folder
              </Button>
            </Box>
            {selectedDrivePickerFolder ? (
              <Stack flexDirection={'row'} gap={1} alignItems={'center'}>
                <Typography noWrap variant="body2">
                  {selectedDrivePickerFolder.name}
                </Typography>
                <IconButton onClick={() => setSelectedDrivePickerFolder(null)}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            ) : null}
          </Stack>
          {selectedPickerFolderIsInDocs ? (
            <Grow in={true}>
              <Alert sx={{ mt: 2 }} severity="error">
                The selected folder is already in the list of selected documents
                to move. Please select a different folder.
              </Alert>
            </Grow>
          ) : null}
        </>
      )}
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const id =
            selectedDrive && selectedDrivePickerFolder
              ? selectedDrivePickerFolder.id
              : selectedDrive
              ? selectedDrive.id
              : selectedFolder;
          mutate(id);
        }}
        mutateLoading={isMoving}
      />
      {/* permission to actually do the move to the selected folder*/}
      {openPermissionCheckDialog ? (
        <SharedDrivesPermissionCheckDialog
          open={openPermissionCheckDialog}
          handleClose={() => {
            setOpenPermissionCheckDialog(false);
            setSelectedDrive(null);
          }}
          minimumRequiredRole="organizer"
          folderOrDriveId={selectedDrive?.id || selectedFolder}
          actionAfterPermissionGranted={() => {
            // do normal action
            if (
              isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanMoveDocs.value)
            ) {
              setOpenChangeLogDialog(true);
            } else {
              if (openingPicker) {
                handleOpenPicker();
              } else {
                const id =
                  selectedDrive && selectedDrivePickerFolder
                    ? selectedDrivePickerFolder.id
                    : selectedDrive
                    ? selectedDrive.id
                    : selectedFolder;
                mutate(id);
              }
            }
          }}
          nameOfSharedDrive={selectedDrive?.name || null}
        />
      ) : null}
    </Box>
  );
}
